import type {
  BannerSwap,
  BannerSwapOverlapAnalysis,
  BannerSwapOverlapGeometry,
  BannerSwapOverlapResolutionPreview,
} from "../../../utils/@types";
import {
  toBannerSwapDate,
  formatSwapDateCell,
  isPermanentSwapEnd,
} from "../../BannerDashboard/bannerDashboardUtils";
import { isValid } from "date-fns";
import { computeTailIncomingPriorityEarlyEnd } from "./bannerSwapTailIncomingEnd";

/** True when half-open intervals [start, end) overlap. End times are exclusive. */
export function bannerSwapIntervalsOverlap(
  startA: Date,
  endA: Date,
  startB: Date,
  endB: Date
): boolean {
  const a0 = startA.getTime();
  const a1 = endA.getTime();
  const b0 = startB.getTime();
  const b1 = endB.getTime();
  return a0 < b1 && b0 < a1;
}

function getChannelEffectiveDates(
  swap: BannerSwap,
  channelId: string
): { startDate: Date; endDate: Date } {
  const override = swap.channelSchedules?.[channelId];
  if (override) {
    return {
      startDate: toBannerSwapDate(override.startDate),
      endDate: toBannerSwapDate(override.endDate),
    };
  }
  return {
    startDate: toBannerSwapDate(swap.startDate),
    endDate: toBannerSwapDate(swap.endDate),
  };
}

function isPermanentEnd(date: Date): boolean {
  return isPermanentSwapEnd(date);
}

function isFullyWithinExistingSwapBlock(
  incomingStart: Date,
  incomingEnd: Date,
  blockStart: Date,
  blockEnd: Date
): boolean {
  return (
    incomingStart.getTime() >= blockStart.getTime() &&
    incomingEnd.getTime() <= blockEnd.getTime()
  );
}

function isFullyWithinAnyConflictingSwap(
  incomingStart: Date,
  incomingEnd: Date,
  conflictingSwaps: BannerSwapOverlapAnalysis["conflicts"][number]["conflictingSwaps"]
): boolean {
  for (const swap of conflictingSwaps) {
    const blockStart = toBannerSwapDate(swap.startDate);
    const blockEnd = toBannerSwapDate(swap.endDate);
    if (
      !isValid(blockStart) ||
      !isValid(blockEnd) ||
      !isFullyWithinExistingSwapBlock(
        incomingStart,
        incomingEnd,
        blockStart,
        blockEnd
      )
    ) {
      continue;
    }
    return true;
  }
  return false;
}

export function deriveOverlapGeometry(
  startDate: Date,
  endDate: Date,
  analysis: BannerSwapOverlapAnalysis
): BannerSwapOverlapGeometry {
  for (const conflict of analysis.conflicts) {
    for (const swap of conflict.conflictingSwaps) {
      const peerStart = toBannerSwapDate(swap.startDate);
      const peerEnd = toBannerSwapDate(swap.endDate);
      if (
        !isValid(peerStart) ||
        !isValid(peerEnd) ||
        !bannerSwapIntervalsOverlap(startDate, endDate, peerStart, peerEnd) ||
        startDate.getTime() >= peerStart.getTime()
      ) {
        continue;
      }
      return "incoming_starts_first";
    }
  }
  return "incoming_starts_later";
}

function earliestOverlappingPeerStart(
  incomingStart: Date,
  incomingEnd: Date,
  conflictingSwaps: BannerSwapOverlapAnalysis["conflicts"][number]["conflictingSwaps"]
): { start: Date; swapUID: string } | null {
  let earliest: Date | null = null;
  let swapUID = "";
  for (const swap of conflictingSwaps) {
    const peerStart = toBannerSwapDate(swap.startDate);
    const peerEnd = toBannerSwapDate(swap.endDate);
    if (
      !isValid(peerStart) ||
      !isValid(peerEnd) ||
      !bannerSwapIntervalsOverlap(
        incomingStart,
        incomingEnd,
        peerStart,
        peerEnd
      ) ||
      incomingStart.getTime() >= peerStart.getTime()
    ) {
      continue;
    }
    if (!earliest || peerStart.getTime() < earliest.getTime()) {
      earliest = peerStart;
      swapUID = swap.swapUID;
    }
  }
  return earliest ? { start: earliest, swapUID } : null;
}

function latestBlockerEndForChannel(
  analysis: BannerSwapOverlapAnalysis,
  channelId: string
): Date | null {
  const conflict = analysis.conflicts.find((c) => c.channelId === channelId);
  if (!conflict?.conflictingSwaps.length) {
    return null;
  }
  let latest: Date | null = null;
  for (const swap of conflict.conflictingSwaps) {
    const end = toBannerSwapDate(swap.endDate);
    if (!isValid(end)) {
      continue;
    }
    if (!latest || end.getTime() > latest.getTime()) {
      latest = end;
    }
  }
  return latest;
}

export function analyzeBannerSwapOverlaps(params: {
  channelIDs: string[];
  startDate: Date;
  endDate: Date;
  queue: BannerSwap[];
  excludeSwapUID?: string;
}): BannerSwapOverlapAnalysis {
  const { channelIDs, startDate, endDate, queue, excludeSwapUID } = params;
  const conflictMap = new Map<
    string,
    BannerSwapOverlapAnalysis["conflicts"][number]["conflictingSwaps"]
  >();
  const conflictingSwapUIDs = new Set<string>();

  if (!isValid(startDate) || !isValid(endDate)) {
    return {
      hasConflicts: false,
      conflictChannelIds: [],
      cleanChannelIds: [...channelIDs],
      conflicts: [],
      conflictingSwapUIDs: [],
    };
  }

  for (const existing of queue) {
    if (excludeSwapUID && existing.swapUID === excludeSwapUID) {
      continue;
    }
    if (!["scheduled", "running"].includes(existing.status)) {
      continue;
    }

    for (const channelId of channelIDs) {
      if (!existing.channelIDs.includes(channelId)) {
        continue;
      }
      const peerDates = getChannelEffectiveDates(existing, channelId);
      if (
        !isValid(peerDates.startDate) ||
        !isValid(peerDates.endDate) ||
        !bannerSwapIntervalsOverlap(
          startDate,
          endDate,
          peerDates.startDate,
          peerDates.endDate
        )
      ) {
        continue;
      }

      conflictingSwapUIDs.add(existing.swapUID);
      const list = conflictMap.get(channelId) ?? [];
      if (!list.some((s) => s.swapUID === existing.swapUID)) {
        list.push({
          swapUID: existing.swapUID,
          swapName: existing.swapName,
          startDate: peerDates.startDate.toISOString(),
          endDate: peerDates.endDate.toISOString(),
          status: existing.status,
          overlappingChannelIds: [channelId],
          totalChannelCount: existing.channelIDs.length,
        });
      }
      conflictMap.set(channelId, list);
    }
  }

  const conflictChannelIds = channelIDs.filter((id) => conflictMap.has(id));
  const cleanChannelIds = channelIDs.filter((id) => !conflictMap.has(id));

  return {
    hasConflicts: conflictChannelIds.length > 0,
    conflictChannelIds,
    cleanChannelIds,
    conflicts: conflictChannelIds.map((channelId) => ({
      channelId,
      conflictingSwaps: (conflictMap.get(channelId) ?? []).sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      ),
    })),
    conflictingSwapUIDs: Array.from(conflictingSwapUIDs),
  };
}

export function buildOverlapResolutionPreview(params: {
  channelIDs: string[];
  startDate: Date;
  endDate: Date;
  analysis: BannerSwapOverlapAnalysis;
  overlapGeometry: BannerSwapOverlapGeometry;
}): BannerSwapOverlapResolutionPreview {
  const { channelIDs, startDate, endDate, analysis, overlapGeometry } = params;
  const wrongGeometryTail =
    "Not available when your swap starts before the existing swap.";
  const wrongGeometryTruncate =
    "Not available when your swap starts on or after the existing swap.";

  const removeChannels = channelIDs.map((channelId) => ({
    channelId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    ...(analysis.conflictChannelIds.includes(channelId)
      ? { note: "Removed from swap" }
      : {}),
  }));

  const removeAvailable = analysis.cleanChannelIds.length > 0;

  let tailExistingUnavailable: string | undefined =
    overlapGeometry === "incoming_starts_first" ? wrongGeometryTail : undefined;
  const tailExistingChannels = channelIDs.map((channelId) => {
    if (!analysis.conflictChannelIds.includes(channelId)) {
      return {
        channelId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    }
    const conflict = analysis.conflicts.find((c) => c.channelId === channelId);
    if (
      overlapGeometry === "incoming_starts_later" &&
      conflict &&
      isFullyWithinAnyConflictingSwap(startDate, endDate, conflict.conflictingSwaps)
    ) {
      tailExistingUnavailable =
        "Your swap dates fall entirely within an existing swap; tail-after-existing is not available.";
    }

    const blockerEnd = latestBlockerEndForChannel(analysis, channelId);
    if (blockerEnd && isPermanentEnd(blockerEnd)) {
      tailExistingUnavailable =
        "One or more blocking swaps are permanent; tail-after-existing is not available.";
    }
    const deferredStart = blockerEnd ?? startDate;
    const deferredEnd = endDate;
    if (
      overlapGeometry === "incoming_starts_later" &&
      blockerEnd &&
      deferredStart.getTime() >= deferredEnd.getTime() &&
      !tailExistingUnavailable
    ) {
      tailExistingUnavailable =
        "Tail-after-existing is not available because the existing swap ends on or after your swap's end date.";
    }
    return {
      channelId,
      startDate: deferredStart.toISOString(),
      endDate: deferredEnd.toISOString(),
      note: blockerEnd
        ? `Starts after blocking swap ends (${formatSwapDateCell(blockerEnd)}); keeps scheduled end date`
        : undefined,
    };
  });

  const tailIncomingChannels = channelIDs.map((channelId) => ({
    channelId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    ...(analysis.conflictChannelIds.includes(channelId)
      ? { note: "Incoming swap takes priority on schedule" }
      : {}),
  }));

  const tailIncomingAdjustedBlockers: BannerSwapOverlapResolutionPreview["tail_incoming_priority"]["adjustedBlockers"] =
    [];
  if (overlapGeometry === "incoming_starts_later") {
    for (const conflict of analysis.conflicts) {
      for (const swap of conflict.conflictingSwaps) {
        const peerStart = toBannerSwapDate(swap.startDate);
        const peerEnd = toBannerSwapDate(swap.endDate);
        if (!isValid(peerStart) || !isValid(peerEnd)) {
          continue;
        }
        const earlyEnd = computeTailIncomingPriorityEarlyEnd({
          incomingStart: startDate,
          peerStart,
          peerEnd,
        });
        if (earlyEnd) {
          tailIncomingAdjustedBlockers.push({
            swapUID: swap.swapUID,
            swapName: swap.swapName,
            channelId: conflict.channelId,
            previousEndDate: peerEnd.toISOString(),
            newEndDate: earlyEnd.toISOString(),
          });
        }
      }
    }
  }

  let truncateUnavailable: string | undefined =
    overlapGeometry === "incoming_starts_later"
      ? wrongGeometryTruncate
      : undefined;
  const truncateChannels = channelIDs.map((channelId) => {
    if (!analysis.conflictChannelIds.includes(channelId)) {
      return {
        channelId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    }
    const conflict = analysis.conflicts.find((c) => c.channelId === channelId);
    const peer = conflict
      ? earliestOverlappingPeerStart(
          startDate,
          endDate,
          conflict.conflictingSwaps
        )
      : null;
    if (!peer) {
      if (!truncateUnavailable) {
        truncateUnavailable =
          "Cannot end your swap early: no valid cut point before the existing swap.";
      }
      return {
        channelId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    }
    if (peer.start.getTime() <= startDate.getTime()) {
      if (!truncateUnavailable) {
        truncateUnavailable =
          "Cannot end your swap early: the existing swap starts on or before yours.";
      }
    }
    return {
      channelId,
      startDate: startDate.toISOString(),
      endDate: peer.start.toISOString(),
      note: `Ends when swap #${peer.swapUID} starts`,
    };
  });

  let delayUnavailable: string | undefined =
    overlapGeometry === "incoming_starts_later"
      ? wrongGeometryTruncate
      : undefined;
  const delayChannels = channelIDs.map((channelId) => ({
    channelId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    ...(analysis.conflictChannelIds.includes(channelId)
      ? { note: "Your swap keeps its scheduled window" }
      : {}),
  }));
  const delayAdjustedPeers: BannerSwapOverlapResolutionPreview["delay_existing_start_priority"]["adjustedPeers"] =
    [];

  if (overlapGeometry === "incoming_starts_first") {
    for (const conflict of analysis.conflicts) {
      for (const swap of conflict.conflictingSwaps) {
        const peerStart = toBannerSwapDate(swap.startDate);
        const peerEnd = toBannerSwapDate(swap.endDate);
        if (
          !isValid(peerStart) ||
          !isValid(peerEnd) ||
          !bannerSwapIntervalsOverlap(startDate, endDate, peerStart, peerEnd) ||
          startDate.getTime() >= peerStart.getTime()
        ) {
          continue;
        }
        if (swap.status === "running") {
          delayUnavailable =
            "Cannot delay a swap that is already running on overlapping channels.";
        }
        const delayedStart = endDate;
        if (delayedStart.getTime() >= peerEnd.getTime()) {
          if (!delayUnavailable) {
            delayUnavailable =
              "Cannot delay the existing swap: your swap ends on or after it would finish.";
          }
          continue;
        }
        delayAdjustedPeers.push({
          swapUID: swap.swapUID,
          swapName: swap.swapName,
          channelId: conflict.channelId,
          previousStartDate: peerStart.toISOString(),
          newStartDate: delayedStart.toISOString(),
        });
      }
    }
    if (
      delayAdjustedPeers.length === 0 &&
      !delayUnavailable
    ) {
      delayUnavailable =
        "Cannot delay the existing swap: no overlapping scheduled swaps to adjust.";
    }
  }

  let dropPeerUnavailable: string | undefined =
    overlapGeometry === "incoming_starts_later"
      ? wrongGeometryTruncate
      : undefined;
  const dropPeerChannels = channelIDs.map((channelId) => ({
    channelId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    ...(analysis.conflictChannelIds.includes(channelId)
      ? { note: "Your swap keeps its scheduled window" }
      : {}),
  }));
  const dropPeerAdjusted: BannerSwapOverlapResolutionPreview["drop_peer_conflicts"]["adjustedPeers"] =
    [];

  if (overlapGeometry === "incoming_starts_first") {
    const seenPeerChannel = new Set<string>();
    const removalCountBySwap = new Map<string, number>();
    for (const conflict of analysis.conflicts) {
      for (const swap of conflict.conflictingSwaps) {
        const peerStart = toBannerSwapDate(swap.startDate);
        const peerEnd = toBannerSwapDate(swap.endDate);
        if (
          !isValid(peerStart) ||
          !isValid(peerEnd) ||
          !bannerSwapIntervalsOverlap(startDate, endDate, peerStart, peerEnd) ||
          startDate.getTime() >= peerStart.getTime()
        ) {
          continue;
        }
        if (swap.status === "running") {
          dropPeerUnavailable =
            "Cannot remove channels from a swap that is already running on overlapping channels.";
          continue;
        }
        if (swap.status !== "scheduled") {
          continue;
        }
        const key = `${swap.swapUID}:${conflict.channelId}`;
        if (seenPeerChannel.has(key)) {
          continue;
        }
        seenPeerChannel.add(key);
        removalCountBySwap.set(
          swap.swapUID,
          (removalCountBySwap.get(swap.swapUID) ?? 0) + 1
        );
      }
    }
    for (const conflict of analysis.conflicts) {
      for (const swap of conflict.conflictingSwaps) {
        const key = `${swap.swapUID}:${conflict.channelId}`;
        if (!seenPeerChannel.has(key)) {
          continue;
        }
        const removals = removalCountBySwap.get(swap.swapUID) ?? 1;
        const remaining = Math.max(0, swap.totalChannelCount - removals);
        dropPeerAdjusted.push({
          swapUID: swap.swapUID,
          swapName: swap.swapName,
          channelId: conflict.channelId,
          action: remaining === 0 ? "cancel_swap" : "remove_channel",
          remainingChannelCount: remaining,
        });
      }
    }
    if (dropPeerAdjusted.length === 0 && !dropPeerUnavailable) {
      dropPeerUnavailable =
        "No scheduled conflicting swaps can have channels removed.";
    }
  }

  return {
    remove_conflicts: {
      available: removeAvailable,
      unavailableReason: removeAvailable
        ? undefined
        : "All selected channels overlap; none would remain.",
      channels: removeChannels,
    },
    tail_existing_priority: {
      available: !tailExistingUnavailable,
      unavailableReason: tailExistingUnavailable,
      channels: tailExistingChannels,
    },
    tail_incoming_priority: {
      available:
        overlapGeometry === "incoming_starts_later" &&
        tailIncomingAdjustedBlockers.length > 0,
      unavailableReason:
        overlapGeometry === "incoming_starts_first"
          ? wrongGeometryTail
          : tailIncomingAdjustedBlockers.length === 0
            ? "Existing swap cannot be shortened for this overlap."
            : undefined,
      channels: tailIncomingChannels,
      adjustedBlockers: tailIncomingAdjustedBlockers,
    },
    truncate_incoming_priority: {
      available: !truncateUnavailable,
      unavailableReason: truncateUnavailable,
      channels: truncateChannels,
    },
    delay_existing_start_priority: {
      available: !delayUnavailable,
      unavailableReason: delayUnavailable,
      channels: delayChannels,
      adjustedPeers: delayAdjustedPeers,
    },
    drop_peer_conflicts: {
      available: !dropPeerUnavailable && dropPeerAdjusted.length > 0,
      unavailableReason: dropPeerUnavailable,
      channels: dropPeerChannels,
      adjustedPeers: dropPeerAdjusted,
    },
  };
}

export function buildClientOverlapCheckResult(params: {
  channelIDs: string[];
  startDate: Date;
  endDate: Date;
  queue: BannerSwap[];
  excludeSwapUID?: string;
}): BannerSwapOverlapAnalysis {
  const analysis = analyzeBannerSwapOverlaps(params);
  const overlapGeometry = deriveOverlapGeometry(
    params.startDate,
    params.endDate,
    analysis
  );
  const preview = buildOverlapResolutionPreview({
    channelIDs: params.channelIDs,
    startDate: params.startDate,
    endDate: params.endDate,
    analysis,
    overlapGeometry,
  });
  return { ...analysis, overlapGeometry, preview };
}

export function overlapSummaryText(analysis: BannerSwapOverlapAnalysis): string {
  if (!analysis.hasConflicts) {
    return "";
  }
  const channelCount = analysis.conflictChannelIds.length;
  const swapCount = analysis.conflictingSwapUIDs.length;
  return `${channelCount} channel${channelCount === 1 ? "" : "s"} overlap with ${swapCount} scheduled swap${swapCount === 1 ? "" : "s"}.`;
}
